/**
 * "Ver vinculados" del Explorador de Datos (ONTO-1): pasar del conjunto
 * filtrado actual al conjunto de objetos vinculados de otro tipo.
 *
 * El pivote no recorre registro a registro: junta los valores DISTINTOS del
 * campo de enlace en el conjunto de origen (un GROUP BY en /explore/query) y
 * abre el tipo destino con `campo_destino in [valores]`. Un vínculo many-to-many
 * con tabla intermedia (`through`) son dos saltos: origen -> tabla -> destino.
 *
 * Funciones puras; el store hace las llamadas.
 */
import type { FilterRow } from '../../store/explorerStore';

/** Tope de valores de enlace por salto (el backend también corta en 1000). */
export const PIVOT_VALUE_CAP = 1000;
/** Página del GROUP BY de valores distintos (límite de /explore/query). */
export const PIVOT_PAGE_SIZE = 500;

/** Un vínculo tal como lo responde GET /object-types/links/all (snake_case). */
export interface RawOntologyLink {
  id: string;
  source_object_type_id: string;
  target_object_type_id: string;
  relationship_type?: string;
  join_keys?: { source_field?: string; target_field?: string }[];
  through?: { object_type_id?: string; source_key?: string; target_key?: string } | null;
  normalize_keys?: boolean;
  label?: string | null;
}

export type PivotDisabledReason = 'composite' | 'normalized' | 'no_keys' | 'unknown_type';

export interface PivotOption {
  /** Estable: `<linkId>-fwd` o `<linkId>-rev`. */
  key: string;
  linkId: string;
  label: string | null;
  reverse: boolean;
  targetTypeId: string;
  /** Campo del tipo de origen cuyos valores se juntan. */
  anchorField: string;
  /** Campo del tipo destino que se filtra con `in`. */
  matchField: string;
  through: { objectTypeId: string; anchorKey: string; matchKey: string } | null;
  disabledReason: PivotDisabledReason | null;
}

/** Los pivotes posibles desde `typeId`, en ambas direcciones de cada vínculo. */
export function pivotOptions(
  links: RawOntologyLink[],
  typeId: string,
  knownTypeIds?: Set<string>,
): PivotOption[] {
  const out: PivotOption[] = [];
  for (const link of links) {
    const dirs: boolean[] = [];
    if (link.source_object_type_id === typeId) dirs.push(false);
    if (link.target_object_type_id === typeId) dirs.push(true);
    for (const reverse of dirs) {
      const keys = (link.join_keys || []).map((jk) => ({
        source: String(jk.source_field || '').trim(),
        target: String(jk.target_field || '').trim(),
      }));
      const first = keys[0];
      const targetTypeId = reverse ? link.source_object_type_id : link.target_object_type_id;
      const thr = link.through && link.through.object_type_id
        ? {
            objectTypeId: String(link.through.object_type_id),
            anchorKey: String((reverse ? link.through.target_key : link.through.source_key) || ''),
            matchKey: String((reverse ? link.through.source_key : link.through.target_key) || ''),
          }
        : null;
      let disabledReason: PivotDisabledReason | null = null;
      if (!first || !first.source || !first.target || (thr && (!thr.anchorKey || !thr.matchKey))) {
        disabledReason = 'no_keys';
      } else if (keys.length > 1 && !thr) {
        disabledReason = 'composite';
      } else if (link.normalize_keys) {
        disabledReason = 'normalized';
      } else if (knownTypeIds && !knownTypeIds.has(targetTypeId)) {
        disabledReason = 'unknown_type';
      }
      out.push({
        key: `${link.id}-${reverse ? 'rev' : 'fwd'}`,
        linkId: link.id,
        label: link.label || null,
        reverse,
        targetTypeId,
        anchorField: first ? (reverse ? first.target : first.source) : '',
        matchField: first ? (reverse ? first.source : first.target) : '',
        through: thr,
        disabledReason,
      });
    }
  }
  return out;
}

/** Valores de enlace limpios de las filas de un GROUP BY (sin vacíos ni repetidos). */
export function keyValuesFromGroups(rows: Record<string, unknown>[], into?: Set<string>): Set<string> {
  const out = into ?? new Set<string>();
  for (const r of rows) {
    const v = r.group_key;
    if (v === null || v === undefined) continue;
    const s = typeof v === 'string' ? v : String(v);
    if (s.trim() === '') continue;
    out.add(s);
  }
  return out;
}

/** Filtros del Explorador en la forma de /explore/query (`in` manda la lista). */
export function toWireFilters(filters: FilterRow[]): { field: string; op: string; value: string | string[] }[] {
  return filters.map(({ field, op, value, values }) => ({
    field,
    op,
    value: op === 'in' ? (values ?? []) : value,
  }));
}

/** El cuerpo del GROUP BY que junta los valores distintos de `field`. */
export function distinctValuesBody(typeId: string, filters: FilterRow[], field: string, offset: number) {
  return {
    object_type_id: typeId,
    filters: toWireFilters(filters),
    aggregate: { function: 'COUNT', field: '*' },
    group_by: field,
    limit: PIVOT_PAGE_SIZE,
    offset,
    with_count: false,
  };
}

/** La fila de filtro `in` que abre el conjunto destino. */
export function buildPivotFilter(id: string, field: string, values: Iterable<string>, fromLabel: string): FilterRow {
  const list = Array.from(values).slice(0, PIVOT_VALUE_CAP);
  return { id, field, op: 'in', value: '', values: list, pivotFrom: fromLabel };
}

// ── Migas ──────────────────────────────────────────────────────────────────

export interface PivotCrumb {
  typeId: string;
  typeName: string;
  filters: FilterRow[];
  /** Cómo se llegó a este conjunto (campo de enlace o etiqueta del vínculo). */
  via?: string;
  count?: number | null;
  /** Se usaron solo los primeros PIVOT_VALUE_CAP valores de enlace. */
  truncated?: boolean;
}

/** Texto corto de los filtros de un conjunto: "state = TX, seats > 100". */
export function describeFilters(filters: FilterRow[]): string {
  const sym: Record<string, string> = { eq: '=', neq: '≠', gt: '>', gte: '>=', lt: '<', lte: '<=' };
  return filters
    .filter((f) => f.field && f.op !== 'in')
    .map((f) => {
      if (f.op === 'is_null') return `${f.field} ∅`;
      if (f.op === 'is_not_null') return `${f.field} ≠ ∅`;
      if (f.op === 'contains') return `${f.field} ~ ${f.value}`;
      if (f.op === 'starts_with') return `${f.field} ^ ${f.value}`;
      return `${f.field} ${sym[f.op] ?? f.op} ${f.value}`;
    })
    .join(', ');
}

/**
 * El rastro después de pivotar desde el conjunto actual: la última miga se
 * actualiza con los filtros que el usuario tenga ahora (pudo agregar más sobre
 * el conjunto vinculado) y se agrega la miga del destino.
 */
export function pushPivotCrumb(trail: PivotCrumb[], current: PivotCrumb, next: PivotCrumb): PivotCrumb[] {
  const base = trail.length ? trail.slice(0, -1) : [];
  return [...base, current, next];
}

/** Volver a la miga `index`: lo posterior se descarta; en la raíz no queda rastro. */
export function truncateTrail(trail: PivotCrumb[], index: number): PivotCrumb[] {
  if (index <= 0) return [];
  return trail.slice(0, index + 1);
}
